import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface LoginForm {
  email: string;
  password: string;
}

// Hook to detect mobile
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return isMobile;
}

// Slide 1: Data insights visualization
const DataInsightsIllustration = () => (
  <svg viewBox="0 0 300 250" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%', maxWidth: 280 }}>
    <circle cx="150" cy="125" r="95" fill="url(#bgGlow1)" opacity="0.3"/>
    <circle cx="150" cy="125" r="70" fill="url(#bgGlow1)" opacity="0.2"/>
    <circle cx="150" cy="125" r="40" fill="#ffffff" stroke="#1e40af" strokeWidth="3"/>
    <circle cx="150" cy="125" r="30" fill="#eff6ff"/>
    <path d="M140 120 L148 128 L162 114" stroke="#1e40af" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
    <circle cx="150" cy="55" r="18" fill="#1e40af"/>
    <rect x="142" y="48" width="16" height="3" rx="1" fill="white"/>
    <rect x="144" y="54" width="12" height="3" rx="1" fill="white"/>
    <rect x="142" y="60" width="16" height="3" rx="1" fill="white"/>
    <circle cx="220" cy="100" r="16" fill="#F4D03F"/>
    <path d="M212 100 L220 92 L228 100 L220 108 Z" fill="white"/>
    <circle cx="220" cy="160" r="18" fill="#1e40af"/>
    <rect x="212" y="152" width="6" height="16" rx="1" fill="white"/>
    <rect x="222" y="158" width="6" height="10" rx="1" fill="white"/>
    <circle cx="150" cy="200" r="16" fill="#bfdbfe"/>
    <circle cx="150" cy="200" r="6" fill="white"/>
    <circle cx="80" cy="160" r="18" fill="#F4D03F"/>
    <path d="M72 160 L80 152 L88 160 M80 152 V168" stroke="white" strokeWidth="3" strokeLinecap="round"/>
    <circle cx="80" cy="95" r="16" fill="#bfdbfe"/>
    <path d="M73 95 L80 88 L87 95 L80 102 Z" stroke="white" strokeWidth="2" fill="none"/>
    <path d="M150 85 L150 95" stroke="#bfdbfe" strokeWidth="2" strokeDasharray="4 2"/>
    <path d="M175 110 L200 100" stroke="#bfdbfe" strokeWidth="2" strokeDasharray="4 2"/>
    <path d="M175 140 L205 155" stroke="#bfdbfe" strokeWidth="2" strokeDasharray="4 2"/>
    <path d="M150 165 L150 180" stroke="#bfdbfe" strokeWidth="2" strokeDasharray="4 2"/>
    <path d="M125 140 L95 155" stroke="#bfdbfe" strokeWidth="2" strokeDasharray="4 2"/>
    <path d="M120 115 L100 100" stroke="#bfdbfe" strokeWidth="2" strokeDasharray="4 2"/>
    <circle cx="110" cy="60" r="4" fill="#bfdbfe"/>
    <circle cx="195" cy="70" r="3" fill="#dbeafe"/>
    <circle cx="240" cy="130" r="4" fill="#F4D03F"/>
    <circle cx="200" cy="200" r="3" fill="#bfdbfe"/>
    <circle cx="100" cy="190" r="4" fill="#dbeafe"/>
    <circle cx="60" cy="125" r="3" fill="#F4D03F"/>
    <defs>
      <radialGradient id="bgGlow1" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#1e40af"/>
        <stop offset="100%" stopColor="#1e40af" stopOpacity="0"/>
      </radialGradient>
    </defs>
  </svg>
);

// Slide 2: Dashboard with multiple charts
const DashboardIllustration = () => (
  <svg viewBox="0 0 300 250" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%', maxWidth: 280 }}>
    <circle cx="150" cy="125" r="90" fill="rgba(191, 219, 254, 0.4)" />
    <rect x="50" y="50" width="200" height="150" rx="8" fill="#2d2d2d" />
    <rect x="55" y="55" width="190" height="140" rx="5" fill="#ffffff" />
    <rect x="55" y="55" width="190" height="20" rx="5" fill="#eff6ff" />
    <circle cx="70" cy="65" r="4" fill="#1e40af" />
    <circle cx="82" cy="65" r="4" fill="#F4D03F" />
    <circle cx="94" cy="65" r="4" fill="#4caf50" />
    <rect x="65" y="85" width="80" height="50" rx="3" fill="#faf6f2" />
    <rect x="75" y="115" width="10" height="15" fill="#F4D03F" />
    <rect x="90" y="105" width="10" height="25" fill="#1e40af" />
    <rect x="105" y="95" width="10" height="35" fill="#F4D03F" />
    <rect x="120" y="110" width="10" height="20" fill="#bfdbfe" />
    <rect x="155" y="85" width="80" height="50" rx="3" fill="#faf6f2" />
    <circle cx="195" cy="110" r="18" fill="#eff6ff" stroke="#1e40af" strokeWidth="8" strokeDasharray="40 100" />
    <circle cx="195" cy="110" r="18" fill="none" stroke="#F4D03F" strokeWidth="8" strokeDasharray="30 100" strokeDashoffset="-40" />
    <circle cx="195" cy="110" r="18" fill="none" stroke="#bfdbfe" strokeWidth="8" strokeDasharray="30 100" strokeDashoffset="-70" />
    <rect x="65" y="140" width="170" height="45" rx="3" fill="#faf6f2" />
    <path d="M75 170 L100 160 L130 165 L160 150 L190 155 L220 145" stroke="#1e40af" strokeWidth="2.5" fill="none" strokeLinecap="round" />
    <circle cx="75" cy="170" r="3" fill="#1e40af" />
    <circle cx="100" cy="160" r="3" fill="#1e40af" />
    <circle cx="130" cy="165" r="3" fill="#1e40af" />
    <circle cx="160" cy="150" r="3" fill="#1e40af" />
    <circle cx="190" cy="155" r="3" fill="#1e40af" />
    <circle cx="220" cy="145" r="3" fill="#1e40af" />
    <rect x="60" y="210" width="40" height="4" rx="2" fill="#bfdbfe" />
    <rect x="110" y="210" width="80" height="4" rx="2" fill="#F4D03F" />
    <rect x="200" y="210" width="50" height="4" rx="2" fill="#1e40af" />
  </svg>
);

// Slide 3: Cloud sync and mobile access
const CloudSyncIllustration = () => (
  <svg viewBox="0 0 300 250" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%', maxWidth: 280 }}>
    <circle cx="150" cy="130" r="90" fill="rgba(191, 219, 254, 0.3)" />
    <ellipse cx="150" cy="130" rx="95" ry="35" fill="#eff6ff" stroke="#1e40af" strokeWidth="2"/>
    <circle cx="85" cy="115" r="30" fill="#eff6ff" stroke="#1e40af" strokeWidth="2"/>
    <circle cx="130" cy="95" r="35" fill="#eff6ff" stroke="#1e40af" strokeWidth="2"/>
    <circle cx="175" cy="90" r="38" fill="#eff6ff" stroke="#1e40af" strokeWidth="2"/>
    <circle cx="215" cy="105" r="32" fill="#eff6ff" stroke="#1e40af" strokeWidth="2"/>
    <ellipse cx="150" cy="125" rx="85" ry="28" fill="#eff6ff"/>
    <circle cx="90" cy="115" r="24" fill="#eff6ff"/>
    <circle cx="130" cy="100" r="28" fill="#eff6ff"/>
    <circle cx="175" cy="95" r="30" fill="#eff6ff"/>
    <circle cx="210" cy="108" r="26" fill="#eff6ff"/>
    <path d="M95 158 L75 178" stroke="#1e40af" strokeWidth="3" strokeLinecap="round" opacity="0.6"/>
    <path d="M150 162 L150 178" stroke="#1e40af" strokeWidth="3" strokeLinecap="round" opacity="0.6"/>
    <path d="M205 158 L225 178" stroke="#1e40af" strokeWidth="3" strokeLinecap="round" opacity="0.6"/>
    <rect x="50" y="183" width="60" height="38" rx="4" fill="#37474f"/>
    <rect x="54" y="187" width="52" height="30" rx="2" fill="#eff6ff"/>
    <rect x="45" y="221" width="70" height="5" rx="2" fill="#455a64"/>
    <circle cx="80" cy="202" r="8" fill="#1e40af" opacity="0.2"/>
    <path d="M76 202 L79 205 L85 199" stroke="#1e40af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <rect x="125" y="183" width="50" height="65" rx="5" fill="#37474f"/>
    <rect x="129" y="188" width="42" height="52" rx="2" fill="#eff6ff"/>
    <circle cx="150" cy="245" r="3" fill="#546e7a"/>
    <rect x="135" y="197" width="30" height="4" rx="2" fill="#bfdbfe"/>
    <rect x="135" y="205" width="22" height="4" rx="2" fill="#F4D03F"/>
    <circle cx="150" cy="223" r="10" fill="#1e40af" opacity="0.2"/>
    <path d="M146 223 L149 226 L155 220" stroke="#1e40af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <rect x="200" y="183" width="35" height="58" rx="5" fill="#37474f"/>
    <rect x="204" y="188" width="27" height="45" rx="2" fill="#eff6ff"/>
    <circle cx="217" cy="237" r="3" fill="#546e7a"/>
    <rect x="208" y="195" width="18" height="3" rx="1" fill="#1e40af"/>
    <rect x="208" y="201" width="14" height="3" rx="1" fill="#F4D03F"/>
    <circle cx="217" cy="215" r="8" fill="#1e40af" opacity="0.2"/>
    <path d="M213 215 L216 218 L222 212" stroke="#1e40af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// Slide 4: Real-time reports
const ReportsIllustration = () => (
  <svg viewBox="0 0 300 250" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%', maxWidth: 280 }}>
    <circle cx="150" cy="125" r="90" fill="rgba(191, 219, 254, 0.4)" />
    <rect x="90" y="80" width="120" height="150" rx="5" fill="#bfdbfe" transform="rotate(-5 150 155)" />
    <rect x="90" y="75" width="120" height="150" rx="5" fill="#eff6ff" transform="rotate(3 150 150)" />
    <rect x="90" y="70" width="120" height="150" rx="5" fill="#ffffff" stroke="#e5ddd0" strokeWidth="1" />
    <rect x="100" y="85" width="100" height="8" rx="2" fill="#1e40af" />
    <rect x="100" y="100" width="80" height="4" rx="2" fill="#e0e0e0" />
    <rect x="100" y="110" width="90" height="4" rx="2" fill="#e0e0e0" />
    <rect x="100" y="120" width="70" height="4" rx="2" fill="#e0e0e0" />
    <rect x="100" y="135" width="100" height="40" rx="3" fill="#faf6f2" />
    <path d="M110 165 L130 155 L150 160 L170 145 L190 150" stroke="#1e40af" strokeWidth="2" fill="none" />
    <path d="M110 165 L130 155 L150 160 L170 145 L190 150 L190 170 L110 170 Z" fill="rgba(30, 64, 175, 0.2)" />
    <rect x="100" y="185" width="100" height="25" rx="2" fill="#faf6f2" />
    <line x1="100" y1="195" x2="200" y2="195" stroke="#e0e0e0" strokeWidth="1" />
    <line x1="133" y1="185" x2="133" y2="210" stroke="#e0e0e0" strokeWidth="1" />
    <line x1="166" y1="185" x2="166" y2="210" stroke="#e0e0e0" strokeWidth="1" />
    <circle cx="195" cy="85" r="18" fill="#4caf50" />
    <path d="M187 85 L193 91 L205 79" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
    <circle cx="70" cy="100" r="4" fill="#F4D03F" />
    <circle cx="230" cy="130" r="3" fill="#bfdbfe" />
    <circle cx="240" cy="180" r="5" fill="#1e40af" opacity="0.5" />
    <circle cx="60" cy="170" r="3" fill="#F4D03F" />
  </svg>
);

const slideContent = [
  {
    illustration: <DataInsightsIllustration />,
    title: 'Verileriniz Bir Arada',
    subtitle: 'Tüm iş verilerinizi tek merkezden yönetin ve analiz edin',
  },
  {
    illustration: <DashboardIllustration />,
    title: 'Detaylı Dashboard',
    subtitle: 'Tüm verilerinizi tek bir ekranda görüntüleyin ve analiz edin',
  },
  {
    illustration: <CloudSyncIllustration />,
    title: 'Her Yerden Erişim',
    subtitle: 'Bulut tabanlı sistemle raporlarınıza dilediğiniz cihazdan ulaşın',
  },
  {
    illustration: <ReportsIllustration />,
    title: 'Anlık Raporlar',
    subtitle: 'Otomatik oluşturulan raporlarla kararlarınızı hızlandırın',
  },
];

export function Login() {
  const navigate = useNavigate();
  const { login, isLoading } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<LoginForm>({ email: '', password: '' });
  const [activeSlide, setActiveSlide] = useState(0);
  const [previousSlide, setPreviousSlide] = useState<number | null>(null);
  const [slideDirection, setSlideDirection] = useState<'left' | 'right'>('right');
  const [isAnimating, setIsAnimating] = useState(false);
  const [mounted, setMounted] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (isMobile) return; // Don't auto-slide on mobile
    const interval = setInterval(() => {
      if (!isAnimating) {
        triggerSlideChange((activeSlide + 1) % 4, 'right');
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [activeSlide, isAnimating, isMobile]);

  const triggerSlideChange = (newIndex: number, direction: 'left' | 'right') => {
    setPreviousSlide(activeSlide);
    setSlideDirection(direction);
    setIsAnimating(true);
    setActiveSlide(newIndex);
    setTimeout(() => {
      setIsAnimating(false);
      setPreviousSlide(null);
    }, 600);
  };

  const handleDotClick = (index: number) => {
    if (isAnimating || index === activeSlide) return;
    const direction = index > activeSlide ? 'right' : 'left';
    triggerSlideChange(index, direction);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      await login(formData.email, formData.password);
      // Her zaman Online Ciro Takip sayfasına yönlendir
      navigate('/', { replace: true });
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: { message?: string } } };
      const rawMessage = axiosError.response?.data?.message || (err instanceof Error ? err.message : 'Login failed');

      // Hata mesajlarını Türkçe'ye çevir
      const errorTranslations: Record<string, string> = {
        'Invalid email or password': 'Geçersiz e-posta veya şifre',
        'Invalid credentials': 'Geçersiz kimlik bilgileri',
        'User not found': 'Kullanıcı bulunamadı',
        'Wrong password': 'Yanlış şifre',
        'Email is required': 'E-posta gerekli',
        'Password is required': 'Şifre gerekli',
        'Invalid email format': 'Geçersiz e-posta formatı',
        'Account is disabled': 'Hesap devre dışı',
        'Too many login attempts': 'Çok fazla giriş denemesi',
        'Login failed': 'Giriş başarısız',
        'Network Error': 'Bağlantı hatası',
      };

      setError(errorTranslations[rawMessage] || rawMessage);
    } finally {
      setLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.loadingSpinner} />
      </div>
    );
  }

  const currentSlide = slideContent[activeSlide];

  // Mobile Layout
  if (isMobile) {
    return (
      <>
        <style>{keyframes}</style>
        <div style={mobileStyles.wrapper}>
          <div style={{
            ...mobileStyles.container,
            opacity: mounted ? 1 : 0,
            transform: mounted ? 'translateY(0)' : 'translateY(10px)',
          }}>
            {/* Logo */}
            <div style={mobileStyles.logoSection}>
              <div style={mobileStyles.heroTitle}>
                <span style={mobileStyles.heroRapor}>Rapor</span>
                <span style={mobileStyles.heroGor}>Gör</span>
              </div>
              <div style={mobileStyles.heroX}>×</div>
              <div style={mobileStyles.heroBesyildiz}>Beşyıldız</div>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} style={mobileStyles.form}>
              {error && (
                <div style={mobileStyles.errorBox}>
                  <span>{error}</span>
                  <button
                    type="button"
                    onClick={() => setError(null)}
                    style={mobileStyles.errorClose}
                  >
                    ×
                  </button>
                </div>
              )}

              <div style={mobileStyles.inputGroup}>
                <label style={mobileStyles.label}>E-posta</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={e => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  style={mobileStyles.input}
                  placeholder="ornek@sirket.com"
                  required
                  autoComplete="email"
                />
              </div>

              <div style={mobileStyles.inputGroup}>
                <label style={mobileStyles.label}>Şifre</label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={e => setFormData(prev => ({ ...prev, password: e.target.value }))}
                  style={mobileStyles.input}
                  placeholder="••••••••"
                  required
                  autoComplete="current-password"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                style={{
                  ...mobileStyles.submitButton,
                  opacity: loading ? 0.7 : 1,
                }}
              >
                {loading ? (
                  <span style={mobileStyles.buttonLoader} />
                ) : (
                  'Giriş Yap'
                )}
              </button>
            </form>

            {/* Footer */}
            <div style={mobileStyles.footer}>
              <span>RaporGör, </span>
              <a
                href="https://newtontech.com.tr"
                target="_blank"
                rel="noopener noreferrer"
                style={mobileStyles.footerLink}
              >
                NewtonTech
              </a>
              <span> tarafından sağlanmaktadır.</span>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Desktop Layout
  return (
    <>
      <style>{keyframes}</style>
      <div style={styles.wrapper}>
        <div style={{
          ...styles.container,
          opacity: mounted ? 1 : 0,
          transform: mounted ? 'scale(1)' : 'scale(0.98)',
        }}>
          {/* Left Panel - Branding */}
          <div style={styles.leftPanel}>
            {/* Decorative leaves */}
            <div style={styles.leafTop}>
              <svg width="80" height="60" viewBox="0 0 80 60" fill="none">
                <path d="M70 50 Q50 30 60 5 Q75 25 70 50Z" fill="#4caf50" opacity="0.6"/>
                <path d="M50 55 Q35 40 45 20 Q55 35 50 55Z" fill="#66bb6a" opacity="0.5"/>
              </svg>
            </div>
            <div style={styles.leafBottom}>
              <svg width="100" height="80" viewBox="0 0 100 80" fill="none">
                <path d="M20 10 Q40 30 30 60 Q15 40 20 10Z" fill="#81c784" opacity="0.5"/>
                <path d="M50 5 Q65 25 55 55 Q45 30 50 5Z" fill="#4caf50" opacity="0.4"/>
              </svg>
            </div>

            <div style={styles.leftContent}>
              {/* Slides Container */}
              <div style={styles.slidesContainer}>
                {/* Previous Slide (exiting) */}
                {isAnimating && previousSlide !== null && (
                  <div
                    key={`prev-${previousSlide}`}
                    style={{
                      ...styles.slideItem,
                      animation: `slideOut${slideDirection === 'right' ? 'Left' : 'Right'} 0.6s cubic-bezier(0.4, 0, 0.2, 1) forwards`,
                    }}
                  >
                    <div style={styles.illustrationWrapper}>
                      {slideContent[previousSlide].illustration}
                    </div>
                    <div style={styles.brandingText}>
                      <h2 style={styles.brandingTitle}>{slideContent[previousSlide].title}</h2>
                      <p style={styles.brandingSubtitle}>{slideContent[previousSlide].subtitle}</p>
                    </div>
                  </div>
                )}

                {/* Current Slide (entering or static) */}
                <div
                  key={`curr-${activeSlide}`}
                  style={{
                    ...styles.slideItem,
                    animation: isAnimating
                      ? `slideIn${slideDirection === 'right' ? 'Right' : 'Left'} 0.6s cubic-bezier(0.4, 0, 0.2, 1) forwards`
                      : 'none',
                  }}
                >
                  <div style={styles.illustrationWrapper}>
                    {currentSlide.illustration}
                  </div>
                  <div style={styles.brandingText}>
                    <h2 style={styles.brandingTitle}>{currentSlide.title}</h2>
                    <p style={styles.brandingSubtitle}>{currentSlide.subtitle}</p>
                  </div>
                </div>
              </div>

              {/* Dot Indicators */}
              <div style={styles.dots}>
                {[0, 1, 2, 3].map(i => (
                  <button
                    key={i}
                    onClick={() => handleDotClick(i)}
                    style={{
                      ...styles.dot,
                      ...(activeSlide === i ? styles.dotActive : {}),
                    }}
                    aria-label={`Slide ${i + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Right Panel - Login Form */}
          <div style={styles.rightPanel}>
            <div style={styles.formContainer}>
              {/* Collaboration Logo */}
              <div style={styles.logoContainer}>
                <div style={styles.collabLogo}>
                  <div>
                    <span style={styles.logoRapor}>Rapor</span>
                    <span style={styles.logoGor}>Gör</span>
                  </div>
                  <span style={styles.logoX}>×</span>
                  <span style={styles.logoBesyildiz}>Beşyıldız</span>
                </div>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} style={styles.form}>
                {error && (
                  <div style={styles.errorBox}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="8" x2="12" y2="12" />
                      <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                    <span>{error}</span>
                    <button
                      type="button"
                      onClick={() => setError(null)}
                      style={styles.errorClose}
                    >
                      ×
                    </button>
                  </div>
                )}

                <div style={styles.inputGroup}>
                  <label style={styles.label}>E-posta</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={e => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    style={styles.input}
                    placeholder="ornek@sirket.com"
                    required
                    autoComplete="email"
                  />
                </div>

                <div style={styles.inputGroup}>
                  <label style={styles.label}>Şifre</label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={e => setFormData(prev => ({ ...prev, password: e.target.value }))}
                    style={styles.input}
                    placeholder="••••••••"
                    required
                    autoComplete="current-password"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    ...styles.submitButton,
                    opacity: loading ? 0.7 : 1,
                  }}
                >
                  {loading ? (
                    <span style={styles.buttonLoader} />
                  ) : (
                    'Giriş Yap'
                  )}
                </button>
              </form>

              {/* Footer */}
              <div style={styles.footer}>
                <span style={styles.footerText}>RaporGör, </span>
                <a
                  href="https://newtontech.com.tr"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={styles.footerLink}
                >
                  NewtonTech Bilişim Hizmetleri
                </a>
                <span style={styles.footerText}> tarafından sağlanmaktadır.</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

const keyframes = `
  @import url('https://fonts.googleapis.com/css2?family=Nunito:wght@400;500;600;700;800&display=swap');

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
  @keyframes slideInRight {
    0% {
      opacity: 0;
      transform: translateX(60px);
    }
    100% {
      opacity: 1;
      transform: translateX(0);
    }
  }
  @keyframes slideInLeft {
    0% {
      opacity: 0;
      transform: translateX(-60px);
    }
    100% {
      opacity: 1;
      transform: translateX(0);
    }
  }
  @keyframes slideOutLeft {
    0% {
      opacity: 1;
      transform: translateX(0);
    }
    100% {
      opacity: 0;
      transform: translateX(-60px);
    }
  }
  @keyframes slideOutRight {
    0% {
      opacity: 1;
      transform: translateX(0);
    }
    100% {
      opacity: 0;
      transform: translateX(60px);
    }
  }

  input::placeholder {
    color: #9ca3af;
  }

  input:focus {
    outline: none;
    border-color: #1e40af !important;
    box-shadow: 0 0 0 3px rgba(30, 64, 175, 0.1) !important;
  }

  button:hover:not(:disabled) {
    transform: translateY(-1px);
    background: #1d4ed8 !important;
  }

  button:active:not(:disabled) {
    transform: translateY(0);
  }
`;

// Mobile styles
const mobileStyles: Record<string, React.CSSProperties> = {
  wrapper: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(180deg, #eff6ff 0%, #ffffff 100%)',
    padding: '24px 20px',
    boxSizing: 'border-box',
    fontFamily: '"Nunito", -apple-system, sans-serif',
    overflow: 'auto',
  },
  container: {
    width: '100%',
    maxWidth: '400px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    transition: 'all 0.4s ease',
  },
  logoSection: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginBottom: 40,
    paddingTop: 20,
  },
  heroTitle: {
    display: 'flex',
    alignItems: 'baseline',
  },
  heroRapor: {
    fontSize: '36px',
    fontWeight: 400,
    color: '#2d2d2d',
    letterSpacing: '-0.02em',
  },
  heroGor: {
    fontSize: '36px',
    fontWeight: 800,
    color: '#2d2d2d',
    letterSpacing: '-0.02em',
  },
  heroX: {
    fontSize: '24px',
    fontWeight: 300,
    color: '#9ca3af',
    margin: '8px 0',
  },
  heroBesyildiz: {
    fontSize: '32px',
    fontWeight: 700,
    color: '#1e40af',
    letterSpacing: '-0.02em',
  },
  form: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  label: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#374151',
  },
  input: {
    width: '100%',
    padding: '16px',
    fontSize: '16px', // Prevents zoom on iOS
    border: '2px solid #e5e7eb',
    borderRadius: '12px',
    background: '#ffffff',
    color: '#2d2d2d',
    transition: 'all 0.2s ease',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    WebkitAppearance: 'none',
  },
  submitButton: {
    width: '100%',
    padding: '18px',
    fontSize: '16px',
    fontWeight: 700,
    color: '#ffffff',
    background: '#1e40af',
    border: 'none',
    borderRadius: '12px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    marginTop: 8,
    fontFamily: 'inherit',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '56px',
    WebkitAppearance: 'none',
  },
  buttonLoader: {
    width: '22px',
    height: '22px',
    border: '2px solid rgba(255,255,255,0.3)',
    borderTopColor: '#ffffff',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  errorBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '12px 16px',
    background: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: '12px',
    color: '#dc2626',
    fontSize: '14px',
  },
  errorClose: {
    marginLeft: 'auto',
    background: 'none',
    border: 'none',
    color: '#dc2626',
    fontSize: '22px',
    cursor: 'pointer',
    padding: '0 4px',
    lineHeight: 1,
    minWidth: '44px',
    minHeight: '44px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  footer: {
    textAlign: 'center',
    marginTop: 32,
    fontSize: '12px',
    color: '#9ca3af',
  },
  footerLink: {
    color: '#1e40af',
    fontWeight: 600,
    textDecoration: 'none',
  },
};

// Desktop styles
const styles: Record<string, React.CSSProperties> = {
  wrapper: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #f0f9ff 0%, #fef6e7 50%, #f0fdf4 100%)',
    padding: '20px',
    boxSizing: 'border-box',
    fontFamily: '"Nunito", -apple-system, sans-serif',
    overflow: 'hidden',
  },
  container: {
    display: 'flex',
    width: '100%',
    maxWidth: '1000px',
    minHeight: '600px',
    maxHeight: '700px',
    background: '#ffffff',
    borderRadius: '24px',
    boxShadow: '0 25px 80px rgba(0, 0, 0, 0.1), 0 10px 30px rgba(0, 0, 0, 0.05)',
    overflow: 'hidden',
    transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
  },
  leftPanel: {
    flex: '1 1 50%',
    background: 'linear-gradient(180deg, #faf6f2 0%, #f5ede3 100%)',
    padding: '40px',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    overflow: 'hidden',
  },
  leafTop: {
    position: 'absolute',
    top: 10,
    right: 10,
    opacity: 0.8,
  },
  leafBottom: {
    position: 'absolute',
    bottom: 10,
    left: 10,
    opacity: 0.8,
  },
  leftContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    position: 'relative',
    zIndex: 1,
  },
  slidesContainer: {
    position: 'relative',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    minHeight: '320px',
  },
  slideItem: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  illustrationWrapper: {
    width: '100%',
    maxWidth: '280px',
    marginBottom: '24px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  brandingText: {
    textAlign: 'center',
    maxWidth: '320px',
  },
  brandingTitle: {
    fontSize: '22px',
    fontWeight: 800,
    color: '#2d2d2d',
    margin: '0 0 10px 0',
    letterSpacing: '-0.01em',
  },
  brandingSubtitle: {
    fontSize: '14px',
    color: '#6b6b6b',
    margin: 0,
    lineHeight: 1.6,
  },
  dots: {
    display: 'flex',
    gap: '10px',
    marginTop: '28px',
  },
  dot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    border: 'none',
    background: 'rgba(30, 64, 175, 0.25)',
    cursor: 'pointer',
    padding: 0,
    transition: 'all 0.3s ease',
  },
  dotActive: {
    background: '#1e40af',
    transform: 'scale(1.2)',
  },
  rightPanel: {
    flex: '1 1 50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px',
    background: '#ffffff',
  },
  formContainer: {
    width: '100%',
    maxWidth: '340px',
  },
  logoContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginBottom: '36px',
  },
  collabLogo: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  logoRapor: {
    fontSize: '28px',
    fontWeight: 400,
    color: '#2d2d2d',
    letterSpacing: '-0.02em',
  },
  logoGor: {
    fontSize: '28px',
    fontWeight: 800,
    color: '#2d2d2d',
    letterSpacing: '-0.02em',
  },
  logoX: {
    fontSize: '20px',
    fontWeight: 300,
    color: '#9ca3af',
  },
  logoBesyildiz: {
    fontSize: '28px',
    fontWeight: 700,
    color: '#1e40af',
    letterSpacing: '-0.02em',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  label: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#374151',
  },
  input: {
    width: '100%',
    padding: '14px 16px',
    fontSize: '15px',
    border: '2px solid #e5e7eb',
    borderRadius: '12px',
    background: '#ffffff',
    color: '#2d2d2d',
    transition: 'all 0.2s ease',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
  },
  submitButton: {
    width: '100%',
    padding: '16px',
    fontSize: '15px',
    fontWeight: 700,
    color: '#ffffff',
    background: '#1e40af',
    border: 'none',
    borderRadius: '12px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    marginTop: '8px',
    fontFamily: 'inherit',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '52px',
  },
  buttonLoader: {
    width: '20px',
    height: '20px',
    border: '2px solid rgba(255,255,255,0.3)',
    borderTopColor: '#ffffff',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  errorBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '12px 16px',
    background: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: '12px',
    color: '#dc2626',
    fontSize: '14px',
  },
  errorClose: {
    marginLeft: 'auto',
    background: 'none',
    border: 'none',
    color: '#dc2626',
    fontSize: '20px',
    cursor: 'pointer',
    padding: '0 4px',
    lineHeight: 1,
  },
  footer: {
    textAlign: 'center',
    marginTop: '40px',
  },
  footerText: {
    fontSize: '12px',
    color: '#9ca3af',
  },
  footerLink: {
    fontSize: '12px',
    color: '#1e40af',
    fontWeight: 600,
    textDecoration: 'none',
  },
  loadingContainer: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #f0f9ff 0%, #fef6e7 50%, #f0fdf4 100%)',
  },
  loadingSpinner: {
    width: '40px',
    height: '40px',
    border: '3px solid rgba(30, 64, 175, 0.2)',
    borderTopColor: '#1e40af',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
};
